import api from '../utils/api'
import { type BreadcrumbT } from '../components/CustomBreadcrumbs'
import { type SurveyCompletionRate } from '../models/Division'
import { type AxiosResponse } from 'axios'
import { type ReportsV2, type SurveyItemRespondersResponse, type SurveyResponseFilter } from '../models/Report'

interface ParamsType {
  termId: string | null
  distributionSeriesId: string | null
  pageSize: number
  byInstructor?: string
  byReportingHierarchy?: string
  mergePdf?: string
  export_by?: string
}

interface CommentsExportParams {
  termId: string | null
  distributionSeriesId: string | null
}

interface CommentsExportPayload {
  slice_ids?: string[]
  survey_item_id?: string
  revision_id?: number
}
export const getBreadCrumbList = async (urlId: string): Promise<BreadcrumbT[]> => {
  const response = await api.get(`/api/divisions/path/${urlId}`)
  return response.data
}

export const getCompletionRate = async (divisionId: string, params?: ParamsType): Promise<SurveyCompletionRate> => {
  const response = await api.get(`/api/divisions/${divisionId}/completion_rates/`, { params })
  return response.data
}

// Export division reports as CSV
export const getDivisionCSVExport = async (divisionId: string, params?: ParamsType): Promise<AxiosResponse> => {
  const responseType = params?.export_by ? 'json' : 'blob'
  return await api.get(`/api/divisions/${divisionId}/reports/export/csv/`, { params, responseType })
}
// Export division reports as PDF
export const getDivisionPDFExport = async (divisionId: string, params?: ParamsType): Promise<AxiosResponse> => {
  const responseType = params?.export_by ? 'json' : 'blob'
  return await api.get(`/api/divisions/${divisionId}/reports/export/pdf/`, { params, responseType })
}

export const getSectionTable = async (divisionId: string, categoryName: string, params: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/divisions/${divisionId}/completion_rates/children/${categoryName}/`, {
    headers: { Accept: 'application/cdg' },
    params
  })
}

interface getReportsV2Params {
  divisionId: string
  params: ParamsType
  data: any
}

export const getReportsV2 = async ({ divisionId, params, data }: getReportsV2Params): Promise<AxiosResponse> => {
  const response = await api.post(`/api/divisions/${divisionId}/reports/v2/?term_id=${params.termId}&distribution_series_id=${params.distributionSeriesId}`, data)
  return response.data
}

export const getDivisionCommentsCSVExport = async (
  divisionId: string,
  params: CommentsExportParams,
  data: CommentsExportPayload = {}
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/divisions/${divisionId}/reports/comments/export/csv/?term_id=${params.termId}&distribution_series_id=${params.distributionSeriesId}`,
    data
  )
}

interface ReportTaskPayload {
  // Division (Academic Unit) only — also apply to every descendant Academic Unit and Course
  // Section. Ignored for Course Sections.
  include_children?: boolean
  // Revert only: omit to revert to the immediately previous version; pass the latest version
  // to restore. Ignored by regenerate.
  target_version?: number
}

// Shared POST for the Account-Owner report-task endpoints (regenerate / revert), which share
// the same `?term_id=&distribution_series_id=` shape and 202 `{ task_id, message, ... }`
// contract. `objectId` is a division or section id; the backend disambiguates.
const postReportTask = async (
  action: 'regenerate' | 'revert',
  objectId: string,
  params: CommentsExportParams,
  data: ReportTaskPayload
): Promise<AxiosResponse> => {
  return await api.post(
    `/api/divisions/${objectId}/reports/${action}/?term_id=${params.termId}&distribution_series_id=${params.distributionSeriesId}`,
    data
  )
}

// Account-Owner-only: clear + regenerate the cached report for an Academic Unit or Course Section.
export const regenerateReport = async (
  objectId: string,
  params: CommentsExportParams,
  data: Pick<ReportTaskPayload, 'include_children'> = {}
): Promise<AxiosResponse> => await postReportTask('regenerate', objectId, params, data)

// Account-Owner-only: flip the live pointer of the cached report to a previous (or the latest)
// version. Rejects with 409 when there is no previous version, it is already latest, the report
// is a rolling-results report, or the report is not yet released.
export const revertReport = async (
  objectId: string,
  params: CommentsExportParams,
  data: ReportTaskPayload = {}
): Promise<AxiosResponse> => await postReportTask('revert', objectId, params, data)

interface getReportsCompareParams {
  params: ParamsType
  divisionId: string
  data: {
    calculations?: string[]
    compareIds?: string[]
    limit?: number
  }
}

export const getCategoryTabsChildren = async (categoryName: string): Promise<any> => {
  const response = await api.get(`/api/acad_units/category_name/${categoryName}/children/`)
  return response.data
}

export const getReportsCompare = async ({ params, divisionId, data }: getReportsCompareParams): Promise<ReportsV2> => {
  const response = await api.post(`/api/divisions/${divisionId}/reports/compare/`, data, { params })
  return response.data
}

export const getHistoricalTrendsReports = async ({ params, divisionId, data }: getReportsCompareParams): Promise<ReportsV2> => {
  const response = await api.post(`/api/divisions/${divisionId}/reports/history/`, data, { params })
  return response.data
}

interface GetReportsRespondersParams {
  params: ParamsType
  divisionId: string
  data: SurveyResponseFilter
}

export const getReportsResponders = async ({
  params,
  divisionId,
  data
}: GetReportsRespondersParams): Promise<SurveyItemRespondersResponse> => {
  const response = await api.post(`/api/divisions/${divisionId}/reports/responders/`, data, { params })
  return response.data
}

const exportCourseCatalogCSV = async (params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get('/api/divisions/export/', { params })
}

export const resetDivisionsByDivisionId = async (divisionId: string): Promise<AxiosResponse> => {
  return await api.delete(`/api/divisions/${divisionId}/children/reset/`)
}

export const exportChildrenCSV = async (divisionId: string): Promise<any> => {
  const response = await api.get(`/api/divisions/${divisionId}/children/export/`)
  return response
}

export const getInstructorCompletionRates = async (divisionId: string, params?: Record<string, any>): Promise<AxiosResponse> => {
  return await api.get(`/api/divisions/${divisionId}/completion_rates/instructors/`, {
    headers: { Accept: 'application/cdg' },
    params
  })
}

export default {
  getBreadCrumbList,
  getCompletionRate,
  getDivisionPDFExport,
  getDivisionCSVExport,
  getSectionTable,
  getCategoryTabsChildren,
  getReportsV2,
  getDivisionCommentsCSVExport,
  regenerateReport,
  revertReport,
  getReportsCompare,
  getReportsResponders,
  getHistoricalTrendsReports,
  exportCourseCatalogCSV,
  resetDivisionsByDivisionId,
  exportChildrenCSV,
  getInstructorCompletionRates
}
